import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { I18nextProvider } from "react-i18next";
import { i18n } from "@tellescope/i18n";
import { Toaster } from "@tellescope/ui";

// Shared imports
import { OrganizationLoader } from "./shared/components/OrganizationLoader";

// Admin imports
import { adminQueryClient, AdminProtectedRoute, AdminQueryProvider } from "./admin/components";
import { AdminAuthProvider } from "./admin/providers";
import { AdminLoginPage, AdminMFAPage, AdminDashboardPage } from "./admin/pages";

// Enduser imports
import { EnduserRouteHandler } from "./enduser/components/EnduserRouteHandler";
import { EnduserQueryProvider } from "./enduser/components/EnduserQueryProvider";
import { EnduserLoginPage } from "./enduser/pages/LoginPage";
import { EnduserRegisterPage } from "./enduser/pages/RegisterPage";
import { EnduserResetPasswordPage } from "./enduser/pages/ResetPasswordPage";
import { OpenLoopChatWidget } from "./enduser/components/OpenLoopChatWidget";

// Legacy route imports - backwards compatible routes for old portal URLs
// Note: List routes (/documents, /events, /content, /book-an-appointment) are now
// handled by EnduserHomePage via useLegacyRouteResolver for schema priority.
// Only parameterized routes and sub-routes remain as explicit routes here.
import { LegacyDocumentsPage } from "./enduser/pages/legacy/LegacyDocumentsPage";
import { FormLinkPage } from "./enduser/pages/legacy/FormLinkPage";
import { BookingPageIframe } from "./enduser/pages/legacy/BookingPageIframe";
import { ContentViewPage } from "./enduser/pages/legacy/ContentViewPage";
import { FormResponseViewPage } from "./enduser/pages/legacy/FormResponseViewPage";
import { EventViewPage } from "./enduser/pages/legacy/EventViewPage";
import { ChatRoomViewPage } from "./enduser/pages/legacy/ChatRoomViewPage";
import { VideoCallPage } from "./enduser/pages/legacy/VideoCallPage";
import { RedirectHandler } from "./enduser/pages/legacy/RedirectHandler";
import { FileDownloadPage } from "./enduser/pages/legacy/FileDownloadPage";
import { UnsubscribePage } from "./enduser/pages/legacy/UnsubscribePage";

// Layout wrapper that provides admin auth context for all /admin/* routes
const AdminLayout = () => (
  <QueryClientProvider client={adminQueryClient}>
    <AdminAuthProvider>
      <Routes>
        <Route path="login" element={<AdminLoginPage />} />
        <Route path="mfa" element={<AdminMFAPage />} />
        <Route
          path="dashboard"
          element={
            <AdminProtectedRoute>
              <AdminQueryProvider>
                <AdminDashboardPage />
              </AdminQueryProvider>
            </AdminProtectedRoute>
          }
        />
        <Route index element={<Navigate to="dashboard" replace />} />
      </Routes>
    </AdminAuthProvider>
  </QueryClientProvider>
);

function App() {
  return (
    <I18nextProvider i18n={i18n}>
      <OrganizationLoader>
        <BrowserRouter>
          <Routes>
            {/* Admin routes — all nested under /admin, with their own auth providers */}
            <Route path="/admin/*" element={<AdminLayout />} />

            {/* Enduser routes — wrapped in EnduserQueryProvider for tanstack query access */}
            <Route
              path="/*"
              element={
                <EnduserQueryProvider>
                  <Routes>
                    <Route path="login" element={<EnduserLoginPage />} />
                    <Route path="register" element={<EnduserRegisterPage />} />
                    <Route path="reset-password" element={<EnduserResetPasswordPage />} />
                    <Route path="unsubscribe" element={<UnsubscribePage />} />

                    {/* Legacy routes - backwards compatible with old portal URLs */}
                    {/* Note: Single-segment list routes (/documents, /events, /content, /book-an-appointment)
                        are handled by EnduserHomePage via useLegacyRouteResolver, which checks portal schema
                        first and only falls back to legacy behavior if no matching page exists. */}

                    {/* Sub-routes under /documents - these have specific category filters */}
                    <Route
                      path="documents/form-responses"
                      element={
                        <EnduserRouteHandler>
                          <LegacyDocumentsPage category="submitted" />
                        </EnduserRouteHandler>
                      }
                    />
                    <Route
                      path="documents/outstanding-forms"
                      element={
                        <EnduserRouteHandler>
                          <LegacyDocumentsPage category="outstanding" />
                        </EnduserRouteHandler>
                      }
                    />
                    <Route
                      path="documents/submitted-forms"
                      element={
                        <EnduserRouteHandler>
                          <LegacyDocumentsPage category="submitted" />
                        </EnduserRouteHandler>
                      }
                    />
                    <Route
                      path="documents/files"
                      element={
                        <EnduserRouteHandler>
                          <LegacyDocumentsPage category="files" />
                        </EnduserRouteHandler>
                      }
                    />

                    {/* Parameterized legacy routes - for viewing individual items */}
                    <Route
                      path="content/:id"
                      element={
                        <EnduserRouteHandler>
                          <ContentViewPage />
                        </EnduserRouteHandler>
                      }
                    />
                    <Route
                      path="documents/form-responses/:id"
                      element={
                        <EnduserRouteHandler>
                          <FormResponseViewPage />
                        </EnduserRouteHandler>
                      }
                    />
                    <Route
                      path="events/:id"
                      element={
                        <EnduserRouteHandler>
                          <EventViewPage />
                        </EnduserRouteHandler>
                      }
                    />
                    <Route
                      path="communications/:roomId"
                      element={
                        <EnduserRouteHandler>
                          <ChatRoomViewPage />
                        </EnduserRouteHandler>
                      }
                    />
                    <Route
                      path="video-call"
                      element={
                        <EnduserRouteHandler>
                          <VideoCallPage />
                        </EnduserRouteHandler>
                      }
                    />

                    {/* Iframe legacy routes (form submission and booking page embed) */}
                    <Route
                      path="link/form"
                      element={
                        <EnduserRouteHandler>
                          <FormLinkPage />
                        </EnduserRouteHandler>
                      }
                    />
                    <Route
                      path="book-an-appointment/:pageId"
                      element={
                        <EnduserRouteHandler>
                          <BookingPageIframe />
                        </EnduserRouteHandler>
                      }
                    />

                    {/* File download route - handles direct file links sent to endusers */}
                    <Route
                      path="files/:secureName"
                      element={
                        <EnduserRouteHandler>
                          <FileDownloadPage />
                        </EnduserRouteHandler>
                      }
                    />

                    {/* Shortened link redirect - passes through to API for white-labeled domains */}
                    <Route path="r/:token" element={<RedirectHandler />} />

                    {/* Legacy redirects */}
                    <Route path="home" element={<Navigate to="/" replace />} />

                    {/* Root and portal pages */}
                    <Route path=":pageSlug?" element={<EnduserRouteHandler />} />
                  </Routes>
                </EnduserQueryProvider>
              }
            />
          </Routes>

          <Toaster />
          <OpenLoopChatWidget />
        </BrowserRouter>
      </OrganizationLoader>
    </I18nextProvider>
  );
}

export default App;
